import React from 'react';
import PropTypes from 'prop-types';
import {createRoot} from 'react-dom/client';

// Create a notification container
const createNotificationContainer = () => {
    let container = document.getElementById('notification-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'notification-container';
        container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      pointer-events: none;
    `;
        document.body.appendChild(container);
    }
    return container;
};

// Notification component
const Notification = ({message, type = 'info', onClose, duration = 5000}) => {
    const [open, setOpen] = React.useState(true);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            setOpen(false);
            setTimeout(onClose, 300); // Wait for animation to complete
        }, duration);

        return () => clearTimeout(timer);
    }, [duration, onClose]);

    const handleClose = () => {
        setOpen(false);
        setTimeout(onClose, 300);
    };

    const getBackgroundColor = () => {
        switch (type) {
            case 'success':
                return '#4caf50';
            case 'error':
                return '#f44336';
            case 'warning':
                return '#ff9800';
            case 'info':
            default:
                return '#2196f3';
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return '✓';
            case 'error':
                return '✕';
            case 'warning':
                return '⚠';
            case 'info':
            default:
                return 'ℹ';
        }
    };

    return (
        <div
            style={{
                backgroundColor: getBackgroundColor(),
                color: 'white',
                padding: '12px 20px',
                borderRadius: '4px',
                marginBottom: '10px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                minWidth: '300px',
                maxWidth: '400px',
                pointerEvents: 'auto',
                transform: open ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.3s ease-in-out',
                opacity: open ? 1 : 0,
            }}
        >
            <div style={{display: 'flex', alignItems: 'center', flex: 1}}>
        <span style={{marginRight: '10px', fontSize: '18px'}}>
          {getIcon()}
        </span>
                <span style={{fontSize: '14px', lineHeight: '1.4'}}>
          {message}
        </span>
            </div>
            <button
                onClick={handleClose}
                style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '18px',
                    marginLeft: '10px',
                    padding: '0',
                    width: '20px',
                    height: '20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                ×
            </button>
        </div>
    );
};

Notification.propTypes = {
    message: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
    onClose: PropTypes.func.isRequired,
    duration: PropTypes.number
};

// Show notification function
export const showNotification = (message, type = 'info', duration = 5000) => {
    const container = createNotificationContainer();

    // Create a dedicated host element per-notification to avoid multiple roots on the same container
    const notificationNode = document.createElement('div');
    container.appendChild(notificationNode);

    const root = createRoot(notificationNode);

    const removeNotification = () => {
        root.unmount();
        // Safely remove the host node after unmount
        if (notificationNode.parentNode) {
            notificationNode.parentNode.removeChild(notificationNode);
        }
        // Optional: clean up the container if it has no more children
        if (container.childNodes.length === 0 && container.parentNode) {
            container.parentNode.removeChild(container);
        }
    };

    root.render(
        <Notification
            message={message}
            type={type}
            onClose={removeNotification}
            duration={duration}
        />
    );
};

// Convenience functions
export const showSuccess = (message, duration = 5000) => {
    showNotification(message, 'success', duration);
};

export const showError = (message, duration = 7000) => {
    showNotification(message, 'error', duration);
};

export const showWarning = (message, duration = 6000) => {
    showNotification(message, 'warning', duration);
};

export const showInfo = (message, duration = 5000) => {
    showNotification(message, 'info', duration);
};

// Form validation error display
export const showFormErrors = (errors) => {
    if (Object.keys(errors).length === 0) return;

    const errorMessages = Object.values(errors).filter(msg => msg && typeof msg === 'string');

    if (errorMessages.length === 0) return;
    
    const message = errorMessages.length === 1
        ? errorMessages[0]
        : `Please fix the following errors:\n• ${errorMessages.join('\n• ')}`;

    showError(message);
};

// API error handling
export const handleApiError = (error, defaultMessage = 'An error occurred') => {
    let message;

    if (error.response) {
        const {data, status} = error.response;

        if (data && data.message) {
            message = data.message;
        } else if (data && data.error) {
            message = data.error;
        } else {
            switch (status) {
                case 400:
                    message = 'Invalid request. Please check your input.';
                    break;
                case 401:
                    message = 'You are not authorized to perform this action.';
                    break;
                case 403:
                    message = 'Access denied. You do not have permission.';
                    break;
                case 404:
                    message = 'The requested resource was not found.';
                    break;
                case 409:
                    message = 'This record already exists.';
                    break;
                case 422:
                    message = 'Validation failed. Please check your input.';
                    break;
                case 500:
                    message = 'Server error. Please try again later.';
                    break;
                default:
                    message = `Error ${status}: ${data || 'Unknown error'}`;
            }
        }
    } else if (error.request) {
        // Network error
        message = 'Network error. Please check your connection.';
    } else {
        // Other error
        message = error.message || defaultMessage;
    }

    showError(message);
};

// Success messages for common operations
export const showCreateSuccess = (entityName) => {
    showSuccess(`${entityName} created successfully!`);
};

export const showUpdateSuccess = (entityName) => {
    showSuccess(`${entityName} updated successfully!`);
};

export const showDeleteSuccess = (entityName) => {
    showSuccess(`${entityName} deleted successfully!`);
};

export const showUploadSuccess = (entityName) => {
    showSuccess(`${entityName} uploaded successfully!`);
};

// Loading state management
export const showLoading = (message = 'Loading...') => {
    showInfo(message, 0); // No auto-dismiss
};

export const hideLoading = () => {
    // This would need to be implemented with a more sophisticated state management
    // For now, we'll just show a success message
    showSuccess('Operation completed!');
};
